import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { logSelectChapter } from '@app/analytics';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import useChapterStatus from '@app/domains/common/hooks/useChapterStatus';
import { getChapterPath } from '@app/domains/common/utils/path';
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';

type Props = {
    novel: NovelItem;
    chapter?: ChapterItem | undefined;
    renderedFrom: string;
};

export default function ChapterLinks({ novel, chapter, renderedFrom }: Props) {
    const { hasNextChapter, hasPrevChapter } = useChapterStatus({ chapter });

    const { isChapterLocked } = useChapterLockStatus({ novel, chapter });

    if (isChapterLocked) return null;

    return (
        <section data-posthog-params={JSON.stringify({ on: renderedFrom })} className="pb-10">
            {hasNextChapter && (
                <div className="mt-[40px] flex justify-center">
                    <Link
                        to={getChapterPath({
                            novelSlug: novel?.slug ?? '',
                            chapterSlug: chapter?.relatedChapterInfo?.nextChapter?.slug ?? '',
                        })}
                        onClick={() => logSelectChapter(novel, chapter?.relatedChapterInfo?.nextChapter)}
                        state={{ from: renderedFrom }}
                        data-posthog-click-event={ChapterEvents.ClickNextChapter}
                    >
                        <Button disabled={!hasNextChapter} className="h-[44px] w-[210px] sm:h-[56px]">
                            NEXT CHAPTER
                        </Button>
                    </Link>
                </div>
            )}
            {hasPrevChapter && (
                <div className="mt-[16px] text-center">
                    <Link
                        to={getChapterPath({
                            novelSlug: novel?.slug ?? '',
                            chapterSlug: chapter?.relatedChapterInfo?.previousChapter?.slug ?? '',
                        })}
                        onClick={() => logSelectChapter(novel, chapter?.relatedChapterInfo?.previousChapter)}
                        className="font-set-m13 text-gray-t3 underline"
                        state={{ from: renderedFrom }}
                        data-posthog-click-event={ChapterEvents.ClickPreviousChapter}
                    >
                        PREVIOUS CHAPTER
                    </Link>
                </div>
            )}
        </section>
    );
}
